import { useState, useEffect } from 'react';
import {
  Server, Plus, Trash2, Edit3, X, Download, RefreshCw,
  CheckCircle, XCircle, AlertCircle, Loader2,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';

interface ImportWorker {
  id: string;
  name: string;
  organizationId: string;
  importConfigId: string;
  platform: string;
  watchDir: string;
  filePattern: string;
  endpointUrl: string;
  isActive: boolean;
  lastSeenAt: string | null;
  lastError: string | null;
  token: string;
  createdAt: string;
  importConfig?: { id: string; name: string };
  logs?: WorkerLog[];
}

interface WorkerLog {
  id: string;
  level: string;
  message: string;
  meta: string | null;
  createdAt: string;
}

interface ImportConfig {
  id: string;
  name: string;
  sourceType: string;
}

const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-colors';
const labelClass = 'text-sm text-gray-700 block mb-1';

export default function WorkersPage() {
  const { user } = useAuth();
  const orgId = user?.organizationId || '';

  const [workers, setWorkers] = useState<ImportWorker[]>([]);
  const [configs, setConfigs] = useState<ImportConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [selectedWorker, setSelectedWorker] = useState<ImportWorker | null>(null);

  // Form state
  const [form, setForm] = useState({
    name: '', importConfigId: '', platform: 'linux',
    watchDir: '', filePattern: '*.csv',
  });

  useEffect(() => {
    if (!orgId) return;
    loadData();
  }, [orgId]);

  async function loadData() {
    setLoading(true);
    try {
      const [workersRes, configsRes] = await Promise.all([
        api.get(`/workers?organizationId=${orgId}`),
        api.get('/import-configs', { params: { limit: 100 } }),
      ]);
      setWorkers(workersRes.data || []);
      setConfigs((configsRes.data || []).filter((c) => c.sourceType === 'REMOTE_WORKER' && c.isActive));
    } catch { /* silent */ }
    setLoading(false);
  }

  function openCreate() {
    setForm({ name: '', importConfigId: '', platform: 'linux', watchDir: '', filePattern: '*.csv' });
    setEditingId(null);
    setShowForm(true);
  }

  function openEdit(w: ImportWorker) {
    setForm({
      name: w.name, importConfigId: w.importConfigId,
      platform: w.platform, watchDir: w.watchDir,
      filePattern: w.filePattern,
    });
    setEditingId(w.id);
    setShowForm(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    try {
      const payload = { ...form, organizationId: orgId, endpointUrl: window.location.origin };
      if (editingId) {
        await api.put(`/workers/${editingId}`, { name: form.name, watchDir: form.watchDir, filePattern: form.filePattern });
      } else {
        await api.post('/workers', payload);
      }
      setShowForm(false);
      loadData();
    } catch (err: any) {
      alert(`Fehler: ${err.response?.data?.message || err.message}`);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Worker wirklich löschen? Zertifikate werden unwiderruflich gelöscht.')) return;
    await api.delete(`/workers/${id}`);
    loadData();
  }

  async function handleRegenerateToken(id: string) {
    if (!confirm('Token neu generieren? Der Worker muss neu konfiguriert werden.')) return;
    await api.post(`/workers/${id}/regenerate-token`);
    loadData();
  }

  async function handleDownloadPackage(id: string) {
    setDownloadingId(id);
    try {
      const response = await api.get(`/workers/${id}/package`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers['content-disposition'];
      const filename = disposition?.match(/filename="(.+)"/)?.[1] || 'worker.zip';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert(`Download fehlgeschlagen: ${err.message}`);
    }
    setDownloadingId(null);
  }

  function formatDate(iso: string | null): string {
    if (!iso) return 'Nie';
    return new Date(iso).toLocaleDateString('de-AT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  const isOnline = (w: ImportWorker): boolean => {
    if (!w.lastSeenAt) return false;
    const diff = Date.now() - new Date(w.lastSeenAt).getTime();
    return diff < 300000; // 5 min
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Lade...</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Server size={28} className="text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Import Worker</h1>
            <p className="text-sm text-gray-500">
              {workers.filter(isOnline).length}/{workers.length} online
            </p>
          </div>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm">
          <Plus size={16} /> Worker erstellen
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">
                {editingId ? 'Worker bearbeiten' : 'Neuen Worker erstellen'}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className={labelClass}>Name *</label>
                <input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z.B. LV Wien Watcher" required />
                <p className="text-xs text-gray-400 mt-1">Name wird als Zertifikats-CN verwendet</p>
              </div>
              <div>
                <label className={labelClass}>Import-Konfiguration *</label>
                <select className={inputClass} value={form.importConfigId} onChange={(e) => setForm({ ...form, importConfigId: e.target.value })} required>
                  <option value="">— Konfiguration wählen —</option>
                  {configs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <p className="text-xs text-gray-400 mt-1">Bestimmt das Feld-Mapping für den Import</p>
              </div>
              <div>
                <label className={labelClass}>Plattform</label>
                <select className={inputClass} value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
                  <option value="linux">Linux</option>
                  <option value="windows">Windows</option>
                </select>
              </div>
              {!editingId && (<>
                <div>
                  <label className={labelClass}>Überwachtes Verzeichnis *</label>
                  <input className={inputClass} value={form.watchDir} onChange={(e) => setForm({ ...form, watchDir: e.target.value })} placeholder="/var/imports" required />
                </div>
                <div>
                  <label className={labelClass}>Datei-Pattern</label>
                  <input className={inputClass} value={form.filePattern} onChange={(e) => setForm({ ...form, filePattern: e.target.value })} placeholder="*.csv" />
                </div>
              </>)}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-sm">Abbrechen</button>
                <button type="submit" className="btn-primary text-sm">{editingId ? 'Speichern' : 'Erstellen'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Worker List */}
      {workers.length === 0 ? (
        <div className="card text-center py-12">
          <Server size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">Keine Worker definiert.</p>
          <p className="text-xs text-gray-400 mt-1">Erstelle Worker für den dezentralen Import von Daten.</p>
          <button onClick={openCreate} className="btn-primary mt-4 flex items-center gap-1.5 mx-auto text-sm">
            <Plus size={16} /> Ersten Worker erstellen
          </button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {workers.map(w => (
            <div key={w.id} className={`card ${w.isActive ? '' : 'opacity-60'}`}>
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Server size={24} className="text-gray-500" />
                    <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${isOnline(w) ? 'bg-green-500' : 'bg-gray-300'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{w.name}</h3>
                    <p className="text-xs text-gray-500">
                      {w.platform} · {w.importConfig?.name || '—'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleDownloadPackage(w.id)} disabled={downloadingId === w.id}
                    className="p-1.5 text-gray-400 hover:text-primary" title="Worker-Paket herunterladen">
                    {downloadingId === w.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  </button>
                  <button onClick={() => openEdit(w)} className="p-1.5 text-gray-400 hover:text-blue-600"><Edit3 size={14} /></button>
                  <button onClick={() => handleDelete(w.id)} className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
                </div>
              </div>

              {/* Details */}
              <div className="text-xs text-gray-500 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-gray-400 w-28">Status:</span>
                  {w.isActive
                    ? <span className="flex items-center gap-1 text-green-600"><CheckCircle size={12} /> Aktiv</span>
                    : <span className="flex items-center gap-1 text-gray-400"><XCircle size={12} /> Inaktiv</span>}
                  {isOnline(w) && <span className="text-green-500">● Online</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-gray-400 w-28">Letzter Kontakt:</span>
                  {formatDate(w.lastSeenAt)}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-gray-400 w-28">Verzeichnis:</span>
                  {w.watchDir}
                </div>
              </div>

              {/* Error */}
              {w.lastError && (
                <div className="mt-2 flex items-center gap-1 text-xs text-red-600 bg-red-50 rounded px-2 py-1">
                  <AlertCircle size={12} />
                  {w.lastError}
                </div>
              )}

              {/* Token / Actions */}
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                <div className="text-xs">
                  <span className="text-gray-400">Token: </span>
                  <code className="text-gray-600">{w.token.slice(0, 8)}...{w.token.slice(-4)}</code>
                </div>
                <button onClick={() => handleRegenerateToken(w.id)}
                  className="text-xs text-gray-400 hover:text-blue-600 flex items-center gap-1">
                  <RefreshCw size={12} /> Token neu
                </button>
              </div>

              {/* Logs */}
              {w.logs && w.logs.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-400 mb-1">Letzte Logs:</p>
                  {w.logs.slice(0, 3).map(log => (
                    <div key={log.id} className={`text-xs px-2 py-0.5 rounded ${
                      log.level === 'error' ? 'text-red-600 bg-red-50' :
                      log.level === 'warn' ? 'text-yellow-600 bg-yellow-50' : 'text-gray-500'
                    }`}>
                      {log.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
